import {
  type BillingLimit,
  type SettingValueSource,
  isSameScope,
  type SettingDetailDto,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useSettingsDetailsQuery } from '@/shared/services/settings/settings.api';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  SETTING_SCOPE_IDS,
  type SettingScopeId,
  scopeOfId,
} from '../../utils/setting-scopes/setting-scopes.util';
import {
  type TierLimitMap,
  type TierLimitRow,
  toStoredLimits,
  tierLimitMapOf,
} from '../../utils/tier-limit-map/tier-limit-map.util';
import { BILLING_SETTING_KEYS, TIER_LIMITS_KEY } from '../useDirectPrices/useDirectPrices';
import { type OwnedSettingState, useOwnedSetting } from '../useOwnedSetting/useOwnedSetting';

/** What one lever resolves to at one scope when a tier does not override it. */
export interface LimitBase {
  scope: SettingScopeId;
  /** `null` when the server resolved nothing for that scope at all. */
  value: number | null;
  source: SettingValueSource;
}

export interface UseTierLimits {
  state: OwnedSettingState;
  error: ApiError | null;
  refetch: () => void;
  /** What each tier overrides today. A tier absent from it overrides nothing. */
  map: TierLimitMap;
  /** What a lever is worth, per scope, to a tier that does not override it. */
  baseFor: (lever: BillingLimit) => readonly LimitBase[];
  /** Whether the genders currently resolve to different numbers for a lever. */
  isBaseGenderScoped: (lever: BillingLimit) => boolean;
  isOverridden: boolean;
  isDangerous: boolean;
  mayManage: boolean;
  isSaving: boolean;
  /** One tier's overrides, written as part of the WHOLE map. */
  saveRow: (tier: string, row: TierLimitRow) => Promise<void>;
  restoreDefault: () => Promise<void>;
}

/**
 * `billing.tierLimits`, and the base settings its empty cells inherit.
 *
 * ## Why the base is read here at all
 *
 * `EntitlementService.limit()` answers a tier's override **or** the base
 * setting, per field — so an empty cell is not "nothing", it is a number the
 * operator cannot see from this screen unless it is put there. And the base is
 * **gender-scoped** (`nominations.dailyCount` is `['global', 'gender']`) while
 * the override is global-only, so an empty cell can be worth two different
 * numbers and setting an override **erases that differentiation** for the tier.
 * A single inherited number beside the box would be a lie for one gender.
 *
 * The base rows come from the same `BILLING_SETTING_KEYS` request the rest of
 * the page makes — `BILLING_LIMITS` is spread into it precisely so this costs no
 * second round trip.
 *
 * ## Two consumers, which is what earns it a hook
 *
 * The lever board draws it and the per-tier editor writes it; both need the same
 * whole-map composition, and two copies of that would be two chances to write a
 * partial one (see {@link saveRow}).
 */
export function useTierLimits(): UseTierLimits {
  const { can } = useHasPermission();
  const setting = useOwnedSetting(TIER_LIMITS_KEY);

  // The same cache entry `useOwnedSetting` already fills — RTK Query keys on the
  // argument, so this is a read of what is loaded, not a second request. It is
  // here rather than on the shared hook because what it needs is a DIFFERENT
  // key's rows, which "one owned setting" has no business exposing.
  const details = useSettingsDetailsQuery(
    { keys: BILLING_SETTING_KEYS },
    { skip: !can('settings.read') },
  );

  const map = useMemo(() => tierLimitMapOf(setting.value), [setting.value]);

  const baseFor = useCallback(
    (lever: BillingLimit) => basesOf(details.data?.items, lever),
    [details.data],
  );

  const isBaseGenderScoped = useCallback(
    (lever: BillingLimit) => {
      const genders = basesOf(details.data?.items, lever).filter((base) => base.scope !== 'global');

      return new Set(genders.map((base) => base.value)).size > 1;
    },
    [details.data],
  );

  const saveRow = useCallback(
    async (tier: string, row: TierLimitRow) => {
      // The WHOLE map, always. `billing.tierLimits` resolves by merging its
      // stored layer over a coded default of `{}` and a write replaces that
      // layer — so a tier omitted here silently stops overriding.
      await setting.writeGlobal(toStoredLimits({ ...map, [tier]: row }));
    },
    [setting, map],
  );

  return {
    state: setting.state,
    error: setting.error,
    refetch: setting.refetch,
    map,
    baseFor,
    isBaseGenderScoped,
    isOverridden: setting.isOverridden,
    isDangerous: setting.isDangerous,
    mayManage: setting.mayManage,
    isSaving: setting.isSaving,
    saveRow,
    restoreDefault: setting.clearGlobal,
  };
}

/** One lever's effective value at every scope a gender-scoped setting has. */
function basesOf(
  items: readonly SettingDetailDto[] | undefined,
  lever: BillingLimit,
): readonly LimitBase[] {
  const detail = items?.find((item) => item.key === lever);

  return SETTING_SCOPE_IDS.map((scope) => {
    const row = detail?.effective.find((candidate) => isSameScope(candidate, scopeOfId(scope)));

    return {
      scope,
      value: typeof row?.value === 'number' ? row.value : null,
      source: row?.source ?? 'default',
    };
  });
}
